import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Button } from "../Button";
import {
	StyledDocumentPreview,
	StyledFileList,
	StyledFileItem,
} from "../../styles/StyledDocumentUpload";
import PdfIcon from "../../assets/img/icons/pdf.svg";
import ImageIcon from "../../assets/img/icons/image.svg";
import JpegIcon from "../../assets/img/icons/jpeg.svg";
import PngIcon from "../../assets/img/icons/png.svg";
import DeleteIcon from "../../assets/img/icons/delete.svg";
import PreviewIcon from "../../assets/img/icons/view.svg";

export default function DocumentPreview({ files, onFileRemove, onProceed, onBack }) {
	const [filePreviews, setFilePreviews] = useState({});
	const [selectedFile, setSelectedFile] = useState(null);

	useEffect(() => {
		// Generate previews for image files
		const generatePreviews = async () => {
			const previews = {};
			
			for (const fileItem of files) {
				if (fileItem.file.type.startsWith("image/")) {
					try {
						const preview = await createImagePreview(fileItem.file);
						previews[fileItem.id] = preview;
					} catch (error) {
						console.error("Error generating preview for", fileItem.name, error);
					}
				}
			}
			
			setFilePreviews(previews);
		};

		generatePreviews();
	}, [files]);

	const createImagePreview = (file) => {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.onload = (e) => resolve(e.target.result);
			reader.onerror = reject;
			reader.readAsDataURL(file);
		});
	};

	const formatFileSize = (bytes) => {
		if (bytes === 0) return "0 Bytes";
		const k = 1024;
		const sizes = ["Bytes", "KB", "MB", "GB"];
		const i = Math.floor(Math.log(bytes) / Math.log(k));
		return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
	};

	const formatFileType = (type) => {
		const typeMap = {
			"application/pdf": "PDF document",
			"application/msword": "Word document",
			"application/vnd.openxmlformats-officedocument.wordprocessingml.document": "Word document",
			"image/jpeg": "JPEG image",
			"image/png": "PNG image",
			"image/tiff": "TIFF image",
		};
		return typeMap[type] || type;
	};

	const getFileIcon = (fileType) => {
		switch (fileType) {
			case "application/pdf":
				return PdfIcon;
			case "image/jpeg":
			case "image/jpg":
				return JpegIcon;
			case "image/png":
				return PngIcon;
			default:
				if (fileType.startsWith("image/")) {
					return ImageIcon;
				}
				return PdfIcon; // Default to PDF icon
		}
	};

	const handlePreviewClick = (fileItem) => {
		setSelectedFile(selectedFile?.id === fileItem.id ? null : fileItem);
	};

	return (
		<div style={{ padding: "2rem" }}>
			<StyledDocumentPreview>
				<div className="preview-header">
					<h4 className="preview-title" style={{ 
						fontSize: "1.25rem", 
						fontWeight: "600",
						fontFamily: "museo, serif",
						color: "#0F0F0F" 
					}}>
						Document ready
					</h4>
					<span style={{ 
						color: "#4D6382", 
						fontSize: "1rem",
						fontFamily: "museo, serif" 
					}}>
						Review your selection and proceed to process
					</span>
				</div>

				<StyledFileList>
					{files.map((fileItem) => (
						<div key={fileItem.id}>
							<StyledFileItem>
								<div className="file-info">
									<img 
										src={getFileIcon(fileItem.type)} 
										alt="File" 
										className="file-icon"
									/>
									<div className="file-details">
										<p className="file-name">{fileItem.name}</p>
										<p className="file-size">
											{formatFileSize(fileItem.size)} • {formatFileType(fileItem.type)}
										</p>
									</div>
								</div>
								<div className="file-actions" style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
									{fileItem.file.type.startsWith("image/") && (
										<button
											onClick={() => handlePreviewClick(fileItem)}
											aria-label={`Preview ${fileItem.name}`}
											style={{
												padding: "0.5rem 1rem",
												background: selectedFile?.id === fileItem.id ? "#33AF89" : "#F7F8F9",
												color: selectedFile?.id === fileItem.id ? "white" : "#4D6382",
												border: "1px solid #DDE5EF",
												borderRadius: "6px",
												fontSize: "0.875rem",
												fontWeight: "500",
												fontFamily: "museo-sans, sans-serif",
												cursor: "pointer",
												display: "flex",
												alignItems: "center",
												gap: "0.25rem",
												transition: "all 0.2s ease"
											}}
											onMouseEnter={(e) => {
												if (selectedFile?.id !== fileItem.id) {
													e.target.style.background = "#DDE5EF";
													e.target.style.borderColor = "#899BB5";
												}
											}}
											onMouseLeave={(e) => {
												if (selectedFile?.id !== fileItem.id) {
													e.target.style.background = "#F7F8F9";
													e.target.style.borderColor = "#DDE5EF";
												}
											}}
										>
											<img 
												src={PreviewIcon} 
												alt="" 
												style={{ width: "14px", height: "14px" }}
											/>
											{selectedFile?.id === fileItem.id ? "Hide" : "Preview"}
										</button>
									)}
									<button
										onClick={() => onFileRemove(fileItem.id)}
										aria-label={`Remove ${fileItem.name}`}
										style={{
											padding: "0.5rem 1rem",
											background: "#fef2f2",
											color: "#dc2626",
											border: "1px solid #fecaca",
											borderRadius: "6px",
											fontSize: "0.875rem",
											fontWeight: "500",
											fontFamily: "museo-sans, sans-serif",
											cursor: "pointer",
											display: "flex",
											alignItems: "center",
											gap: "0.25rem",
											transition: "all 0.2s ease"
										}}
										onMouseEnter={(e) => {
											e.target.style.background = "#fee2e2";
											e.target.style.borderColor = "#fca5a5";
										}}
										onMouseLeave={(e) => {
											e.target.style.background = "#fef2f2";
											e.target.style.borderColor = "#fecaca";
										}}
									>
										<img 
											src={DeleteIcon} 
											alt="" 
											style={{ width: "14px", height: "14px" }}
										/>
										Remove
									</button>
								</div>
							</StyledFileItem>

							{/* Image Preview */}
							{selectedFile?.id === fileItem.id && filePreviews[fileItem.id] && (
								<div style={{
									marginTop: "1rem",
									padding: "1.5rem",
									background: "#F7F8F9",
									borderRadius: "8px",
									border: "1px solid #DDE5EF"
								}}>
									<div style={{ 
										marginBottom: "1rem",
										fontSize: "1rem",
										fontWeight: "600",
										fontFamily: "museo-sans, sans-serif",
										color: "#0F0F0F"
									}}>
										Preview: {fileItem.name}
									</div>
									<img 
										src={filePreviews[fileItem.id]}
										alt={`Preview of ${fileItem.name}`}
										style={{
											maxWidth: "100%",
											maxHeight: "400px",
											objectFit: "contain",
											border: "1px solid #DDE5EF",
											borderRadius: "8px",
											boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)"
										}}
									/>
								</div>
							)}
						</div>
					))}
				</StyledFileList>
			</StyledDocumentPreview>
		</div>
	);
}

DocumentPreview.propTypes = {
	files: PropTypes.array.isRequired,
	onFileRemove: PropTypes.func.isRequired,
	onProceed: PropTypes.func.isRequired,
	onBack: PropTypes.func.isRequired,
};